export type AnalysisDateRange = { dateFrom: string; dateTo: string };
const day = 86_400_000;
function epoch(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return null;
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time : null;
}
export function validAnalysisDateRange(range: AnalysisDateRange) {
  const from = epoch(range.dateFrom), to = epoch(range.dateTo);
  return from !== null && to !== null && to >= from && (to - from) / day < 31;
}
export function shiftAnalysisDateRange(range: AnalysisDateRange, direction: -1 | 1): AnalysisDateRange | null {
  if (!validAnalysisDateRange(range)) return null;
  const from = epoch(range.dateFrom), to = epoch(range.dateTo);
  if (from === null || to === null) return null;
  const offset = (to - from + day) * direction;
  return { dateFrom: new Date(from + offset).toISOString().slice(0, 10), dateTo: new Date(to + offset).toISOString().slice(0, 10) };
}
export function analysisDatePreset(preset: "today" | "yesterday" | "week", today: string): AnalysisDateRange {
  const time = epoch(today);
  if (time === null) return { dateFrom: today, dateTo: today };
  const start = time - (preset === "week" ? 6 : preset === "yesterday" ? 1 : 0) * day;
  const end = preset === "yesterday" ? start : time;
  return { dateFrom: new Date(start).toISOString().slice(0, 10), dateTo: new Date(end).toISOString().slice(0, 10) };
}
export function analysisDateRangeLabel(range: AnalysisDateRange) {
  const from = epoch(range.dateFrom), to = epoch(range.dateTo);
  if (from === null || to === null) return "Tarih seç";
  const date = (time: number, year = true) => new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "long", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" }).format(time);
  if (from === to) return date(from);
  if (range.dateFrom.slice(0, 7) === range.dateTo.slice(0, 7)) return `${new Date(from).getUTCDate()}–${date(to)}`;
  return `${date(from, range.dateFrom.slice(0, 4) !== range.dateTo.slice(0, 4))} – ${date(to)}`;
}
export function analysisCalendarToday(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export type AnalysisCalendarPeriod = { kind: "day" | "rolling" | "custom" | "week-to-date" | "week-full" | "month-full" } | { kind: "month-to-date"; anchorDay: number };
export type AnalysisDateSelection = AnalysisDateRange & { period: AnalysisCalendarPeriod };
export type AnalysisCalendarPreset = "today" | "yesterday" | "week" | "this-week" | "last-week" | "this-month" | "last-month";
const iso = (value: number) => new Date(value).toISOString().slice(0, 10);
function calendarMonth(range: AnalysisDateRange, offset: number, period: AnalysisCalendarPeriod): AnalysisDateRange | null {
  const time = epoch(range.dateFrom);
  if (time === null || (period.kind === "month-to-date" && (!Number.isInteger(period.anchorDay) || period.anchorDay < 1 || period.anchorDay > 31))) return null;
  const date = new Date(time);
  const start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1);
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset + 1, 0));
  const endDay = period.kind === "month-to-date" ? Math.min(period.anchorDay, last.getUTCDate()) : last.getUTCDate();
  return { dateFrom: iso(start), dateTo: iso(start + (endDay - 1) * day) };
}
export function analysisCalendarPreset(preset: AnalysisCalendarPreset, today: string): AnalysisDateSelection {
  const time = epoch(today);
  if (time === null || preset === "today" || preset === "yesterday" || preset === "week") return { ...analysisDatePreset(preset === "yesterday" ? "yesterday" : preset === "week" ? "week" : "today", today), period: { kind: preset === "week" ? "rolling" : "day" } };
  const date = new Date(time);
  if (preset === "this-week" || preset === "last-week") {
    const monday = time - (date.getUTCDay() + 6) % 7 * day;
    return preset === "this-week" ? { dateFrom: iso(monday), dateTo: today, period: { kind: "week-to-date" } }
      : { dateFrom: iso(monday - 7 * day), dateTo: iso(monday - day), period: { kind: "week-full" } };
  }
  const period: AnalysisCalendarPeriod = preset === "this-month" ? { kind: "month-to-date", anchorDay: date.getUTCDate() } : { kind: "month-full" };
  const range = calendarMonth({ dateFrom: today, dateTo: today }, preset === "this-month" ? 0 : -1, period);
  return { ...(range ?? { dateFrom: today, dateTo: today }), period };
}
export function shiftAnalysisCalendarRange(range: AnalysisDateRange, period: AnalysisCalendarPeriod, direction: -1 | 1): AnalysisDateRange | null {
  if (!validAnalysisDateRange(range)) return null;
  if (period.kind === "month-full" || period.kind === "month-to-date") return calendarMonth(range, direction, period);
  if (period.kind === "week-full" || period.kind === "week-to-date") {
    const from = epoch(range.dateFrom), to = epoch(range.dateTo);
    if (from === null || to === null) return null;
    return { dateFrom: iso(from + direction * 7 * day), dateTo: iso(to + direction * 7 * day) };
  }
  return shiftAnalysisDateRange(range, direction);
}
export function analysisComparisonRange(range: AnalysisDateRange, period: AnalysisCalendarPeriod = { kind: "custom" }): AnalysisDateRange | null {
  return shiftAnalysisCalendarRange(range, period, -1);
}
